import React, { useState, useEffect } from 'react';
import { Plus, Play, Pause, Trash2, History, Users, UserPlus, Filter, Gift, CalendarClock, Info, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  adminListPromoCredits, adminCreatePromoCredit, adminUpdatePromoCredit,
  adminActivatePromoCredit, adminDeactivatePromoCredit, adminDeletePromoCredit,
  adminGetPromoCreditGrants
} from '../../services/api';

const EMPTY = {
  name: '', credits: 10, audience: 'everyone',
  target_user_ids_text: '', // comma or newline separated
  group_filter: {},
  expires_at: '',
  active: true, apply_now: true,
};

const QUICK_AMOUNTS = [10, 25, 50, 100, 250];

const AUDIENCE_LABEL = {
  everyone: 'Everyone (all existing users)',
  new_users: 'New users (automatic on signup)',
  selected_users: 'Selected users (by user IDs)',
  selected_groups: 'Selected group (by filter)',
};

const PromoCreditsTab = () => {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | promo object
  const [grantsFor, setGrantsFor] = useState(null); // promo object being viewed

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminListPromoCredits();
      setPromos(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (form) => {
    try {
      const payload = buildPayload(form);
      if (editing === 'new') {
        const res = await adminCreatePromoCredit(payload);
        setPromos(prev => [res.data, ...prev]);
        if (res.data.granted_now) {
          alert(`Promo created — ${res.data.granted_now} member${res.data.granted_now === 1 ? '' : 's'} awarded ${form.credits} credits.`);
        }
      } else {
        const res = await adminUpdatePromoCredit(editing.promo_id, payload);
        setPromos(prev => prev.map(p => (p.promo_id === editing.promo_id ? res.data : p)));
      }
      setEditing(null);
    } catch (e) {
      alert(e.response?.data?.detail || 'Save failed');
    }
  };

  const handleActivate = async (promo) => {
    if (promo.audience !== 'new_users') {
      if (!window.confirm(`Award ${promo.credits} credit${promo.credits === 1 ? '' : 's'} to matching users now? This runs immediately.`)) return;
    }
    try {
      const res = await adminActivatePromoCredit(promo.promo_id);
      await load();
      if (res.data.granted !== undefined && promo.audience !== 'new_users') {
        alert(`Activated — ${res.data.granted} new grant${res.data.granted === 1 ? '' : 's'} awarded.`);
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Activate failed');
    }
  };

  const handleDeactivate = async (promo) => {
    try {
      await adminDeactivatePromoCredit(promo.promo_id);
      setPromos(prev => prev.map(p => p.promo_id === promo.promo_id ? { ...p, active: false } : p));
    } catch (e) {
      alert('Failed to deactivate');
    }
  };

  const handleDelete = async (promo) => {
    if (!window.confirm(`Delete "${promo.name}"? Past grants are kept for auditing.`)) return;
    try {
      await adminDeletePromoCredit(promo.promo_id);
      setPromos(prev => prev.filter(p => p.promo_id !== promo.promo_id));
    } catch (e) {
      alert('Failed to delete');
    }
  };

  return (
    <div className="space-y-4" data-testid="promo-credits-tab">
      <div className="bg-blue-50 border border-blue-100 text-blue-900 rounded-xl p-3 text-sm flex gap-2">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div>
          Promotional credits are added instantly to the target audience. New-user promos are applied automatically the moment someone registers.
          Credits are tracked separately from purchased credits — spending goes to promo credits first.
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {promos.length} promotion{promos.length === 1 ? '' : 's'}
        </div>
        <Button
          onClick={() => setEditing('new')}
          className="bg-[#009E60] hover:bg-[#007A4B] rounded-full"
          data-testid="new-promo-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> New promotion
        </Button>
      </div>

      {loading ? (
        <div className="bg-white rounded-2xl p-8 text-center text-gray-500">Loading…</div>
      ) : promos.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 text-center">
          <Gift className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <h4 className="font-semibold text-gray-900 mb-1">No promotional credits yet</h4>
          <p className="text-sm text-gray-500">Create your first bonus offer — say, 25 credits for every new signup.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {promos.map(p => (
            <PromoRow
              key={p.promo_id}
              promo={p}
              onEdit={() => setEditing(p)}
              onActivate={() => handleActivate(p)}
              onDeactivate={() => handleDeactivate(p)}
              onDelete={() => handleDelete(p)}
              onViewGrants={() => setGrantsFor(p)}
            />
          ))}
        </div>
      )}

      {editing && (
        <PromoEditModal
          initial={editing === 'new' ? EMPTY : promoToForm(editing)}
          isNew={editing === 'new'}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}

      {grantsFor && (
        <GrantsModal
          promo={grantsFor}
          onClose={() => setGrantsFor(null)}
        />
      )}
    </div>
  );
};

const promoToForm = (p) => ({
  name: p.name || '',
  credits: p.credits || 10,
  audience: p.audience || 'everyone',
  target_user_ids_text: (p.target_user_ids || []).join('\n'),
  group_filter: p.group_filter || {},
  expires_at: p.expires_at ? p.expires_at.slice(0, 10) : '',
  active: p.active !== false,
  apply_now: false,
});

const buildPayload = (form) => {
  const p = {
    name: form.name.trim(),
    credits: parseInt(form.credits) || 0,
    audience: form.audience,
    active: !!form.active,
    apply_now: !!form.apply_now,
  };
  if (form.audience === 'selected_users') {
    p.target_user_ids = form.target_user_ids_text.split(/[\n,\s]+/).map(s => s.trim()).filter(Boolean);
  }
  if (form.audience === 'selected_groups') {
    const gf = {};
    Object.entries(form.group_filter || {}).forEach(([k, v]) => {
      if (v === '' || v === null || v === undefined) return;
      if (k === 'min_age' || k === 'max_age') {
        const n = parseInt(v); if (!Number.isNaN(n)) gf[k] = n;
      } else if (k === 'is_premium') {
        gf[k] = v === 'true' || v === true;
      } else {
        gf[k] = v;
      }
    });
    p.group_filter = gf;
  }
  if (form.expires_at) {
    // Convert date string to ISO end-of-day so the credits are valid through the chosen day
    const d = new Date(form.expires_at);
    d.setUTCHours(23, 59, 59, 0);
    p.expires_at = d.toISOString();
  }
  return p;
};

const PromoRow = ({ promo, onEdit, onActivate, onDeactivate, onDelete, onViewGrants }) => {
  const expired = promo.expires_at && new Date(promo.expires_at) < new Date();
  const audienceIcon = {
    everyone: <Users className="w-4 h-4" />,
    new_users: <UserPlus className="w-4 h-4" />,
    selected_users: <Users className="w-4 h-4" />,
    selected_groups: <Filter className="w-4 h-4" />,
  }[promo.audience] || <Users className="w-4 h-4" />;
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid={`promo-row-${promo.promo_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-semibold text-gray-900 truncate">{promo.name}</h4>
            <span className="bg-[#009E60]/10 text-[#007A4B] text-xs font-medium px-2 py-0.5 rounded-full">
              +{promo.credits} credits
            </span>
            <span className="inline-flex items-center gap-1 text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">
              {audienceIcon}
              {AUDIENCE_LABEL[promo.audience] || promo.audience}
            </span>
            {promo.active ? (
              <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">Active</span>
            ) : (
              <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">Paused</span>
            )}
            {expired && (
              <span className="text-xs font-medium text-red-700 bg-red-50 px-2 py-0.5 rounded-full">Expired</span>
            )}
          </div>
          <div className="text-xs text-gray-500 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>Awarded {promo.total_awarded || 0} member{(promo.total_awarded || 0) === 1 ? '' : 's'} · {promo.total_credits || 0} credits total</span>
            {promo.expires_at && (
              <span className="inline-flex items-center gap-1">
                <CalendarClock className="w-3 h-3" /> Expires {new Date(promo.expires_at).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <Button size="sm" variant="ghost" onClick={onViewGrants} data-testid={`promo-history-${promo.promo_id}`}>
            <History className="w-4 h-4 mr-1" /> History
          </Button>
          {promo.active ? (
            <Button size="sm" variant="ghost" onClick={onDeactivate} className="text-gray-700" data-testid={`promo-deactivate-${promo.promo_id}`}>
              <Pause className="w-4 h-4 mr-1" /> Pause
            </Button>
          ) : (
            <Button size="sm" variant="ghost" onClick={onActivate} className="text-emerald-700" data-testid={`promo-activate-${promo.promo_id}`}>
              <Play className="w-4 h-4 mr-1" /> Activate
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={onEdit} data-testid={`promo-edit-${promo.promo_id}`}>Edit</Button>
          <Button size="sm" variant="ghost" onClick={onDelete} className="text-red-600 hover:bg-red-50" data-testid={`promo-delete-${promo.promo_id}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const PromoEditModal = ({ initial, isNew, onClose, onSave }) => {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const setGf = (k, v) => setForm(prev => ({ ...prev, group_filter: { ...(prev.group_filter || {}), [k]: v } }));

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-lg max-h-[92vh] overflow-hidden shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="promo-edit-modal">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 flex-shrink-0">
          <h3 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>
            {isNew ? 'New promotional credit' : 'Edit promotion'}
          </h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div>
            <Label className="text-xs text-gray-600">Name</Label>
            <Input value={form.name} onChange={(e) => set('name', e.target.value)}
              placeholder="e.g. Welcome Bonus, Independence Day Gift"
              className="mt-1 rounded-lg" data-testid="promo-name" />
          </div>

          <div>
            <Label className="text-xs text-gray-600">Credits to award</Label>
            <div className="flex gap-2 flex-wrap mt-1">
              {QUICK_AMOUNTS.map(n => (
                <button
                  key={n}
                  type="button"
                  onClick={() => set('credits', n)}
                  className={`px-3 py-1.5 rounded-full text-sm ${form.credits === n ? 'bg-[#009E60] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  data-testid={`quick-${n}`}
                >
                  {n}
                </button>
              ))}
              <Input
                type="number" min="1" max="10000"
                value={form.credits}
                onChange={(e) => set('credits', e.target.value)}
                className="w-24 rounded-lg"
                data-testid="promo-credits"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs text-gray-600">Who receives it</Label>
            <Select value={form.audience} onValueChange={(v) => set('audience', v)}>
              <SelectTrigger className="mt-1 rounded-lg" data-testid="promo-audience"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(AUDIENCE_LABEL).map(([k, l]) => (
                  <SelectItem key={k} value={k}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {form.audience === 'selected_users' && (
            <div>
              <Label className="text-xs text-gray-600">User IDs (one per line or comma-separated)</Label>
              <textarea
                value={form.target_user_ids_text}
                onChange={(e) => set('target_user_ids_text', e.target.value)}
                placeholder="user_abc123&#10;user_def456"
                className="w-full mt-1 border border-gray-200 rounded-lg px-3 py-2 text-sm h-24"
                data-testid="promo-user-ids"
              />
              <p className="text-[11px] text-gray-500 mt-1">Copy user IDs from the Users tab.</p>
            </div>
          )}

          {form.audience === 'selected_groups' && (
            <div className="space-y-2 border border-gray-100 rounded-xl p-3">
              <div className="text-xs font-medium text-gray-700">Group filter</div>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Country" value={form.group_filter?.current_country || ''} onChange={(e) => setGf('current_country', e.target.value)} className="rounded-lg text-sm" />
                <Input placeholder="City" value={form.group_filter?.current_city || ''} onChange={(e) => setGf('current_city', e.target.value)} className="rounded-lg text-sm" />
                <Input placeholder="Region" value={form.group_filter?.current_region || ''} onChange={(e) => setGf('current_region', e.target.value)} className="rounded-lg text-sm" />
                <Input placeholder="Gender (man/woman/…)" value={form.group_filter?.gender || ''} onChange={(e) => setGf('gender', e.target.value)} className="rounded-lg text-sm" />
                <Input placeholder="Ethnicity" value={form.group_filter?.ethnicity || ''} onChange={(e) => setGf('ethnicity', e.target.value)} className="rounded-lg text-sm" />
                <Input placeholder="Religion" value={form.group_filter?.religion || ''} onChange={(e) => setGf('religion', e.target.value)} className="rounded-lg text-sm" />
                <Input type="number" placeholder="Min age" value={form.group_filter?.min_age || ''} onChange={(e) => setGf('min_age', e.target.value)} className="rounded-lg text-sm" />
                <Input type="number" placeholder="Max age" value={form.group_filter?.max_age || ''} onChange={(e) => setGf('max_age', e.target.value)} className="rounded-lg text-sm" />
              </div>
              <Select
                value={form.group_filter?.is_premium === true ? 'true' : form.group_filter?.is_premium === false ? 'false' : 'any'}
                onValueChange={(v) => setGf('is_premium', v === 'any' ? null : v === 'true')}
              >
                <SelectTrigger className="rounded-lg text-sm"><SelectValue placeholder="Premium status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any premium status</SelectItem>
                  <SelectItem value="true">Premium members only</SelectItem>
                  <SelectItem value="false">Free members only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label className="text-xs text-gray-600">Expires on (optional)</Label>
            <Input
              type="date"
              value={form.expires_at}
              onChange={(e) => set('expires_at', e.target.value)}
              className="mt-1 rounded-lg"
              data-testid="promo-expires"
            />
            <p className="text-[11px] text-gray-500 mt-1">Grants stay on record for auditing even after this date.</p>
          </div>

          <div className="flex items-center justify-between border-t border-gray-100 pt-3">
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={!!form.active} onCheckedChange={(v) => set('active', v)} data-testid="promo-active" />
              <span>{form.active ? 'Active' : 'Paused'}</span>
            </label>
            {form.audience !== 'new_users' && (
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={!!form.apply_now} onCheckedChange={(v) => set('apply_now', v)} data-testid="promo-apply-now" />
                <span>Award immediately</span>
              </label>
            )}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-100 flex-shrink-0 bg-white">
          <Button variant="ghost" onClick={onClose} data-testid="promo-cancel">Cancel</Button>
          <Button
            onClick={() => onSave(form)}
            disabled={!form.name.trim() || !form.credits}
            className="bg-[#009E60] hover:bg-[#007A4B] rounded-full"
            data-testid="promo-save"
          >
            {isNew ? 'Create promotion' : 'Save changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const GrantsModal = ({ promo, onClose }) => {
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const res = await adminGetPromoCreditGrants(promo.promo_id, { limit: 200 });
        setGrants(res.data.grants);
        setTotal(res.data.total);
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, [promo.promo_id]);

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-lg max-h-[92vh] overflow-hidden shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="grants-modal">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>Grant history</h3>
            <p className="text-xs text-gray-500">{promo.name} · {total} grants</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center text-gray-500 py-8">Loading…</div>
          ) : grants.length === 0 ? (
            <div className="text-center text-gray-500 py-8">No grants yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-500 uppercase border-b border-gray-100">
                <tr>
                  <th className="text-left py-2">Member</th>
                  <th className="text-right py-2">Credits</th>
                  <th className="text-left py-2 pl-3">Reason</th>
                  <th className="text-left py-2 pl-3">When</th>
                </tr>
              </thead>
              <tbody>
                {grants.map(g => (
                  <tr key={g.grant_id} className="border-b border-gray-50">
                    <td className="py-2">
                      <div className="text-gray-900">{g.user?.name || '—'}</div>
                      <div className="text-[11px] text-gray-500">{g.user?.email || g.user_id}</div>
                    </td>
                    <td className="py-2 text-right font-medium text-[#009E60]">+{g.credits}</td>
                    <td className="py-2 pl-3 capitalize text-gray-700">{g.reason}</td>
                    <td className="py-2 pl-3 text-gray-500 text-xs">{new Date(g.awarded_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default PromoCreditsTab;
